import React, { useState, useEffect } from 'react';
import { StockCard } from './StockCard';
import { PostStockModal } from './PostStockModal';
import { PropertyChat } from './PropertyChat';
import { Plus, Search, Filter, SlidersHorizontal, Trash2, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { stockAPI } from '../lib/stock-api';
import { toast } from 'sonner';
import { authService } from '../lib/auth';
import { logger } from '../services/logger.service';

export interface StockPost {
  id: string;
  type: 'BUY' | 'SELL';
  propertyTitle: string;
  propertyType: string;
  location: string;
  price: string;
  bedrooms?: number;
  bathrooms?: number;
  size?: number;
  description: string;
  agentName: string;
  authorId?: string;
  agency: string;
  timestamp: Date;
  status: 'ACTIVE' | 'SOLD' | 'BOUGHT' | 'WITHDRAWN';
  source: 'internal' | 'external'; // Where the post came from
  internalPropertyId?: string; // ID of property in our catalog (for Agent Hub posts)
  externalListingUrl?: string; // External URL (for External Agent posts)
  messages: Array<{
    id: string;
    userId: string;
    userName: string;
    userRole: string;
    text: string;
    image?: string;
    timestamp: Date;
    isSystem?: boolean;
    isInterest?: boolean;
  }>;
}

interface StockSharingFeedProps {
  source: 'internal' | 'external' | 'admin'; // Which portal is viewing
  availableProperties?: Array<{ id: string; title: string; }>; // For Agent Hub
  onViewProperty?: (propertyId: string) => void; // For navigating to internal properties
}

export function StockSharingFeed({ source, availableProperties = [], onViewProperty }: StockSharingFeedProps) {
  const [posts, setPosts] = useState<StockPost[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<StockPost | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'ALL' | 'BUY' | 'SELL'>('ALL');
  const [showFilters, setShowFilters] = useState(false);
  const [filterLocation, setFilterLocation] = useState('');
  const [filterPropertyType, setFilterPropertyType] = useState('');
  const [filterPriceRange, setFilterPriceRange] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'price_asc' | 'price_desc'>('newest');
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isUnavailable, setIsUnavailable] = useState(false);
  const PAGE_SIZE = 30;
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const chatIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  // Load posts from backend on mount
  useEffect(() => {
    loadPosts();

    // Set up polling — cleared immediately if auth fails
    intervalRef.current = setInterval(() => {
      loadPosts();
    }, 10000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Poll for new chat messages while a chat is open
  useEffect(() => {
    if (chatIntervalRef.current) clearInterval(chatIntervalRef.current);
    if (!selectedPost) return;

    chatIntervalRef.current = setInterval(async () => {
      try {
        const freshMessages = await stockAPI.getMessages(selectedPost.id);
        if (freshMessages.length > 0) {
          setSelectedPost(prev => prev ? { ...prev, messages: freshMessages } : prev);
        }
      } catch {
        // keep existing messages
      }
    }, 5000);

    return () => {
      if (chatIntervalRef.current) clearInterval(chatIntervalRef.current);
    };
  }, [selectedPost?.id]);

  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  const loadPosts = async () => {
    try {
      if (posts.length === 0) {
        setIsLoading(true);
      }
      const fetchedPosts = await stockAPI.getPosts();
      setPosts(fetchedPosts);
    } catch (error: any) {
      // 401/403 — stop polling, mark as unavailable
      if (error?.status === 401 || error?.status === 403) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setIsUnavailable(true);
      }
      // Other errors: suppress silently
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePost = async (postData: Omit<StockPost, 'id' | 'timestamp' | 'status' | 'messages'>) => {
    try {
      const newPost = await stockAPI.createPost(postData);
      setPosts([newPost, ...posts]);
      setIsModalOpen(false);
      toast.success('Stock posted successfully!');
    } catch (error: any) {
      if (error?.message?.includes('401') || error?.message?.includes('Unauthorized') || error?.message?.includes('403')) {
        toast.error('You do not have permission to post stock. Contact your administrator.');
      } else if (error?.message?.includes('521') || error?.message?.includes('Server is down')) {
        toast.error('Server is currently unavailable. Please try again later.');
      } else {
        toast.error('Failed to post stock. Please try again.');
      }
    }
  };

  const handleOpenChat = async (post: StockPost) => {
    setSelectedPost(post); // show immediately with existing messages
    try {
      const freshMessages = await stockAPI.getMessages(post.id);
      if (freshMessages.length > 0) {
        const updatedPost = { ...post, messages: freshMessages };
        setSelectedPost(updatedPost);
        setPosts(prev => prev.map(p => p.id === post.id ? updatedPost : p));
      }
    } catch {
      // keep existing messages — no-op
    }
  };

  const handleCloseChat = () => {
    setSelectedPost(null);
  };

  const handleDeletePost = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this stock post? This cannot be undone.')) return;
    
    setIsDeleting(postId);
    try {
      await stockAPI.deletePost(postId);
      setPosts(posts.filter(p => p.id !== postId));
      if (selectedPost?.id === postId) {
        setSelectedPost(null);
      }
      toast.success('Stock post deleted successfully');
    } catch (error) {
      logger.error('Failed to delete post', error as Error, undefined, 'StockSharingFeed');
      toast.error('Failed to delete post');
    } finally {
      setIsDeleting(null);
    }
  };

  const handleSendMessage = async (_postId: string, text: string, _userId?: string, _userName?: string, _userRole?: string, imageUrl?: string | null) => {
    if (!selectedPost || !text.trim()) return;

    const currentUser = authService.getCurrentUser();
    const userId = currentUser?.id || 'anonymous';
    const userName = currentUser?.name || 'Unknown User';
    const userRole = currentUser?.role === 'agent' ? 'Internal Agent'
      : currentUser?.role === 'external-agent' ? 'External Agent'
      : currentUser?.role === 'admin' ? 'Admin'
      : source === 'internal' ? 'Internal Agent' : 'External Agent';

    try {
      const result = await stockAPI.addMessage(
        selectedPost.id,
        userId,
        userName,
        userRole,
        text,
        false,
        imageUrl || undefined
      );

      if (result.post?.id) {
        // Backend returned full updated post — use it
        setSelectedPost(result.post);
        setPosts(prev => prev.map(p => p.id === result.post.id ? result.post : p));
      } else {
        // Backend only returned the new message — append it optimistically
        const updatedPost = {
          ...selectedPost,
          messages: [...selectedPost.messages, result.message]
        };
        setSelectedPost(updatedPost);
        setPosts(prev => prev.map(p => p.id === selectedPost.id ? updatedPost : p));
      }

      toast.success('Message sent!');
    } catch (error: any) {
      if (error?.message?.includes('521') || error?.message?.includes('Server is down')) {
        toast.error('Server is currently unavailable. Please try again later.');
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    }
  };

  const handleExpressInterest = async (postId: string, userId: string, userName: string, userRole: string) => {
    try {
      const result = await stockAPI.addMessage(
        postId, 
        'system',
        'System',
        'system',
        `${userName} has expressed interest in this property.`, 
        true
      );
      
      if (result.post?.id) {
        setPosts(posts.map(post => post.id === postId ? result.post : post));
        if (selectedPost?.id === postId) setSelectedPost(result.post);
      } else {
        // Append the interest message optimistically
        const target = posts.find(p => p.id === postId);
        if (target) {
          const updatedPost = { ...target, messages: [...target.messages, result.message] };
          setPosts(posts.map(p => p.id === postId ? updatedPost : p));
          if (selectedPost?.id === postId) setSelectedPost(updatedPost);
        }
      }

      toast.success('Interest expressed successfully!');
    } catch (error) {
      logger.error('Failed to express interest', error as Error, undefined, 'StockSharingFeed');
      toast.error('Failed to express interest. Please try again.');
    }
  };

  const handleUpdateStatus = async (postId: string, status: 'SOLD' | 'BOUGHT' | 'WITHDRAWN') => {
    const localPost = posts.find(p => p.id === postId) ?? selectedPost;
    try {
      const result = await stockAPI.updateStatus(postId, status);
      // Backend may return full post or just { id, status } — merge with local state to keep all fields
      const updatedPost: StockPost = result.id && result.id !== postId
        ? result  // backend returned something unexpected — use as-is
        : { ...(localPost ?? ({} as StockPost)), ...result, id: postId, status };

      setPosts(posts.map(post => post.id === postId ? updatedPost : post));
      if (selectedPost?.id === postId) setSelectedPost(updatedPost);

      toast.success(`Property marked as ${status}!`);
    } catch (error) {
      logger.error('Failed to update status', error as Error, undefined, 'StockSharingFeed');
      toast.error('Failed to update status. Please try again.');
    }
  };

  // Filter and search posts
  const filteredPosts = posts.filter(post => {
    const matchesSearch = searchQuery === '' ||
      (post.propertyTitle || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (post.location || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (post.propertyType || '').toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = filterType === 'ALL' || post.type === filterType;
    const matchesLocation = filterLocation === '' || (post.location || '').toLowerCase().includes(filterLocation.toLowerCase());
    const matchesPropertyType = filterPropertyType === '' || post.propertyType === filterPropertyType;

    return matchesSearch && matchesType && matchesLocation && matchesPropertyType;
  });

  const sortedPosts = [...filteredPosts].sort((a, b) => {
    const aTime = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
    const bTime = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
    const aPrice = parseFloat(String(a.price).replace(/[^0-9.]/g, '')) || 0;
    const bPrice = parseFloat(String(b.price).replace(/[^0-9.]/g, '')) || 0;
    if (sortBy === 'newest') return bTime - aTime;
    if (sortBy === 'oldest') return aTime - bTime;
    if (sortBy === 'price_asc') return aPrice - bPrice;
    if (sortBy === 'price_desc') return bPrice - aPrice;
    return 0;
  });

  const totalPages = Math.ceil(sortedPosts.length / PAGE_SIZE);
  const paginatedPosts = sortedPosts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="relative bg-black min-h-screen p-8">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold mb-2 text-white">Stock Sharing</h2>
            <p className="text-white/60">Share and discover property opportunities with fellow agents</p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => loadPosts()}
              className="flex items-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 font-medium rounded-xl transition-all"
              title="Refresh posts"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            {!isUnavailable && (
              <motion.button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center gap-2 px-6 py-3 bg-[#FDB022] hover:bg-[#FDB022]/90 text-black font-medium rounded-xl transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Plus className="w-5 h-5" />
                Post Stock
              </motion.button>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="space-y-4">
          <div className="flex gap-4">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                placeholder="Search by location, property type, or keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-[#FDB022]/50 focus:bg-white/10 transition-all"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl border transition-all ${
                showFilters 
                  ? 'bg-[#FDB022]/20 border-[#FDB022] text-[#FDB022]' 
                  : 'bg-white/5 border-white/10 hover:bg-white/10'
              }`}
            >
              <SlidersHorizontal className="w-5 h-5" />
              Filters
            </button>
          </div>

          {/* Quick Type Filter */}
          <div className="flex gap-3">
            {(['ALL', 'BUY', 'SELL'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  filterType === type
                    ? type === 'BUY'
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      : type === 'SELL'
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : 'bg-[#FDB022]/20 text-[#FDB022] border border-[#FDB022]/30'
                    : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
                }`}
              >
                {type}
              </button>
            ))}
          </div>

          {/* Advanced Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-white/80">Sort By</label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[#FDB022]/50"
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="price_asc">Price: Low to High</option>
                        <option value="price_desc">Price: High to Low</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-white/80">Location</label>
                      <input
                        type="text"
                        placeholder="Filter by location..."
                        value={filterLocation}
                        onChange={(e) => setFilterLocation(e.target.value)}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[#FDB022]/50"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-white/80">Property Type</label>
                      <select
                        value={filterPropertyType}
                        onChange={(e) => setFilterPropertyType(e.target.value)}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[#FDB022]/50"
                      >
                        <option value="">All Types</option>
                        <option value="Apartment">Apartment</option>
                        <option value="House">House</option>
                        <option value="Penthouse">Penthouse</option>
                        <option value="Townhouse">Townhouse</option>
                        <option value="Commercial">Commercial</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-white/80">Price Range</label>
                      <select
                        value={filterPriceRange}
                        onChange={(e) => setFilterPriceRange(e.target.value)}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[#FDB022]/50"
                      >
                        <option value="">All Prices</option>
                        <option value="0-1m">Under R 1M</option>
                        <option value="1m-3m">R 1M - R 3M</option>
                        <option value="3m-5m">R 3M - R 5M</option>
                        <option value="5m+">R 5M+</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={() => {
                        setFilterLocation('');
                        setFilterPropertyType('');
                        setFilterPriceRange('');
                      }}
                      className="px-4 py-2 text-sm text-white/60 hover:text-white transition-colors"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Results Count */}
        <div className="mt-4 text-sm text-white/60">
          Showing {Math.min((currentPage - 1) * PAGE_SIZE + 1, sortedPosts.length)}–{Math.min(currentPage * PAGE_SIZE, sortedPosts.length)} of {sortedPosts.length} {sortedPosts.length === 1 ? 'listing' : 'listings'}
        </div>
      </div>

      {/* Posts Feed */}
      <div className="space-y-4">
        {isUnavailable ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/5 flex items-center justify-center">
              <Search className="w-10 h-10 text-white/20" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Stock Sharing Unavailable</h3>
            <p className="text-white/60">This feature is currently unavailable for your account. Contact your administrator.</p>
          </div>
        ) : isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse bg-white/5 border border-white/10 rounded-2xl p-6 h-40" />
            ))}
          </div>
        ) : sortedPosts.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/5 flex items-center justify-center">
              <Search className="w-10 h-10 text-white/20" />
            </div>
            <h3 className="text-xl font-semibold mb-2">No listings found</h3>
            <p className="text-white/60 mb-6">Try adjusting your search or filters</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setFilterType('ALL');
                setFilterLocation('');
                setFilterPropertyType('');
                setFilterPriceRange('');
                setCurrentPage(1);
              }}
              className="px-6 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-all"
            >
              Clear All Filters
            </button>
          </div>
        ) : (
          paginatedPosts.map((post) => (
            <StockCard
              key={post.id}
              post={post}
              onOpenChat={handleOpenChat}
              onViewProperty={onViewProperty}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 text-sm rounded-lg transition-all ${
                  page === currentPage
                    ? 'bg-[#FDB022] text-black font-bold'
                    : 'bg-white/5 hover:bg-white/10 border border-white/10'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Modals */}
      <PostStockModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreatePost}
        source={source === 'admin' ? 'internal' : source}
        availableProperties={availableProperties}
      />

      {selectedPost && (
        <PropertyChat
          post={selectedPost}
          onClose={handleCloseChat}
          onSendMessage={handleSendMessage}
          onExpressInterest={handleExpressInterest}
          onUpdateStatus={handleUpdateStatus}
        />
      )}
    </div>
  );
}